// skills/pilfering/pilferingUI.js
// Pilfering UI - Gatherer-style card layout

import { pilferingSkill } from './pilfering.js';
import { TARGETS, GLOVES, GUILD_DOMAINS, GUILD_REQUIREMENTS, GUILD_CHEST_CONFIG, PILFERER_UNLOCK_LEVELS, PILFERER_SPEED_CONFIG, getDomainItemsAtTier, DOMAIN_TIER_UPGRADES } from './pilferingData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { formatWithCommas, formatNumber, formatTime } from '../../utils/format.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { getItemName, getItemColor, getItemIcon } from '../../data/items/index.js';
import { renderInfoIcon } from '../../ui/infoTooltip.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus, getTalentFlatBonus } from '../../systems/talentTree/talentTree.js';
import { getSkillIcon } from '../../core/skillRegistry.js';

// Track loot multiplier to detect corruption changes
let lastLootMultiplier = 1;

// Track current tab
let currentTab = 'targets';

/**
 * Render domain icons from skill IDs
 * @param {object} domain - Domain object with skillIds array
 * @returns {string} HTML for skill icons
 */
function getDomainIcons(domain) {
    if (!domain.skillIds || domain.skillIds.length === 0) {
        return '📦'; // Fallback icon
    }
    return domain.skillIds.map(skillId => getSkillIcon(skillId)).join('');
}

/**
 * Render compact header with glove upgrades
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.PILFERING);
    const xpPerHour = getXPPerHour(SKILLS.PILFERING);
    const { levelDisplay, progressPercent, progressText } = getSkillProgressInfo(SKILLS.PILFERING);

    // Tool info
    const activeTool = pilferingSkill.getActiveBuff();
    const gloves = Object.values(GLOVES);
    const currentIndex = gloves.findIndex(g => g.id === activeTool.id);
    const nextGloves = gloves[currentIndex + 1];
    const playerGold = gameState.getGold();

    let upgradeHtml = '';
    if (nextGloves) {
        const canAfford = playerGold >= nextGloves.price;
        if (skill.level < nextGloves.level) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextGloves.name} (${nextGloves.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextGloves.price}">${getItemIcon('coins')} ${formatWithCommas(nextGloves.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextGloves.level}</span>
                </span>
            `;
        } else {
            const priceClass = canAfford ? 'affordable' : 'cant-afford';
            upgradeHtml = `
                <span class="compact-upgrade available" onclick="window.pilferingUI.upgradeGloves('${nextGloves.id}')">
                    → ${nextGloves.name} (${nextGloves.speedMultiplier}x)
                    <span class="upgrade-cost ${priceClass}" data-price="${nextGloves.price}">${getItemIcon('coins')} ${formatWithCommas(nextGloves.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ Max</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.PILFERING)}</span>
                    <span class="compact-name">Pilfering</span>
                    ${renderSkillInfo(SKILLS.PILFERING)}
                    ${renderTalentButton(SKILLS.PILFERING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Gloves:</span>
                    <span class="tool-current">${activeTool.name} (${activeTool.speedMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render a single target card
 */
function renderTargetCard(target, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= target.level;
    const isActive = currentAction?.type === 'pilfering' && currentAction?.target?.id === target.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    const speedMultiplier = pilferingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.PILFERING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.PILFERING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = target.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.PILFERING);
    let effectiveXP = target.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier for drop quantities
    const lootMultiplier = getCorruptionLootMultiplier(SKILLS.PILFERING);

    // Build drop display (targets drop coins)
    const drop = target.drops[0];
    const baseQty = drop.quantity;
    const effectiveQty = baseQty * lootMultiplier;
    const qtyDisplay = lootMultiplier > 1
        ? `<span class="drop-qty boosted">x${effectiveQty}</span>`
        : `<span class="drop-qty">x${baseQty}</span>`;

    const cardClasses = [
        'target-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-target-id="${target.id}"
             style="--target-color: ${target.color}">

            <div class="target-card-header">
                <span class="target-card-level">${isUnlocked ? `Lvl ${target.level}` : `Req: ${target.level}`}</span>
                <span class="target-card-icon">👤</span>
                <span class="target-card-name">${target.name}</span>
            </div>

            <div class="target-card-info">
                <div class="target-stat">
                    <span class="stat-icon pilfering-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="target-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="target-drops">
                <div class="drop-info target-drop">
                    <span class="drop-name">${getItemIcon('coins')} Gold</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="target-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.pilferingUI.togglePickpocketing('${target.id}')">
                        ${isActive ? 'Stop' : 'Pickpocket'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="target-progress">
                <div class="target-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render tab bar for switching between targets and guild
 */
function renderTabBar() {
    return `
        <div class="skill-tabs">
            <button class="skill-tab ${currentTab === 'targets' ? 'active' : ''}"
                    onclick="window.pilferingUI.switchTab('targets')">
                🎯 Targets
            </button>
            <button class="skill-tab ${currentTab === 'guild' ? 'active' : ''}"
                    onclick="window.pilferingUI.switchTab('guild')">
                🏛️ Guild
            </button>
        </div>
    `;
}

/**
 * Switch between tabs
 */
export function switchTab(tabId) {
    currentTab = tabId;
    renderPilferingUI();
}

/**
 * Get current tab (for state persistence)
 */
export function getCurrentTab() {
    return currentTab;
}

/**
 * Render the pilfering interface
 */
export function renderPilferingUI(params = {}) {
    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.PILFERING);
    const currentAction = gameState.getCurrentAction();

    // Handle tab from URL params
    if (params.tab) {
        currentTab = params.tab;
    }

    // Update loot multiplier tracker
    lastLootMultiplier = getCorruptionLootMultiplier(SKILLS.PILFERING);

    let tabContent = '';
    if (currentTab === 'guild') {
        tabContent = renderGuildTab();
    } else {
        const targetsHtml = Object.values(TARGETS)
            .map(target => renderTargetCard(target, skill.level, currentAction))
            .join('');
        tabContent = `<div class="target-grid">${targetsHtml}</div>`;
    }

    container.innerHTML = `
        <div class="pilfering-interface">
            ${renderCompactHeader()}
            ${renderTabBar()}
            ${tabContent}
        </div>
    `;
}

/**
 * Toggle pickpocketing on/off for a target
 */
export function togglePickpocketing(targetId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.target?.id === targetId) {
        pilferingSkill.stopPickpocketing();
    } else {
        const result = pilferingSkill.canPickpocket(targetId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            pilferingSkill.startPickpocketing(targetId);
        }
    }

    renderPilferingUI();
}

/**
 * Upgrade gloves
 */
export function upgradeGloves(glovesId) {
    const result = pilferingSkill.upgradeGloves(glovesId);

    if (result.success) {
        renderPilferingUI();
    } else {
        toastWarning(result.reason);
    }
}

// ==================== GUILD UI ====================

/**
 * Render the guild tab content
 */
function renderGuildTab() {
    const isUnlocked = gameState.isGuildUnlocked();

    if (!isUnlocked) {
        return renderGuildUnlockPanel();
    }

    // Reset chest state tracker on fresh render
    const chest = gameState.getGuildChest();
    lastChestState = JSON.stringify(chest);

    return `
        <div class="guild-content">
            ${renderGuildStats()}
            ${renderGuildDomains()}
            ${renderGuildChest()}
        </div>
    `;
}

/**
 * Render unlock requirements panel
 */
function renderGuildUnlockPanel() {
    const canUnlock = pilferingSkill.canUnlockGuild();
    const pilferingLevel = gameState.getSkill(SKILLS.PILFERING).level;
    const hasGuildHall = gameState.hasGuildHallMilestone();
    const goldCount = gameState.getGold();

    const pilferingMet = pilferingLevel >= GUILD_REQUIREMENTS.pilfering;
    const goldMet = goldCount >= GUILD_REQUIREMENTS.gold;

    return `
        <div class="guild-unlock-panel">
            <div class="guild-unlock-header">
                <h3>🏛️ Pilfering Guild</h3>
                <p>Lead the underground network of thieves and ruffians</p>
            </div>
            <div class="guild-unlock-requirements">
                <h4>Unlock Requirements</h4>
                <div class="requirement ${hasGuildHall ? 'met' : 'unmet'}">
                    <span class="req-icon">${hasGuildHall ? '✓' : '✗'}</span>
                    <span class="req-text">Build Guild Hall (Architecting Lv20)</span>
                    <span class="req-status">${hasGuildHall ? 'Built' : 'Not Built'}</span>
                </div>
                <div class="requirement ${pilferingMet ? 'met' : 'unmet'}">
                    <span class="req-icon">${pilferingMet ? '✓' : '✗'}</span>
                    <span class="req-text">Pilfering Level ${GUILD_REQUIREMENTS.pilfering}</span>
                    <span class="req-status">(${pilferingLevel}/${GUILD_REQUIREMENTS.pilfering})</span>
                </div>
                <div class="requirement ${goldMet ? 'met' : 'unmet'}">
                    <span class="req-icon">${goldMet ? '✓' : '✗'}</span>
                    <span class="req-text">${formatNumber(GUILD_REQUIREMENTS.gold)} Gold</span>
                    <span class="req-status">(Have: ${formatNumber(goldCount)})</span>
                </div>
            </div>
            ${canUnlock.success ? `
                <button class="guild-unlock-btn" onclick="window.pilferingUI.unlockGuild()">
                    Create the Guild
                </button>
            ` : `
                <p class="guild-unlock-hint">Meet all requirements to unlock the guild</p>
            `}
            <div class="guild-benefits">
                <h4>Benefits</h4>
                <ul>
                    <li>🧑‍🤝‍🧑 Assign pilferers to gather materials passively</li>
                    <li>📦 Shared chest for collected materials</li>
                    <li>📜 Pilferers can assist with bounty contracts</li>
                </ul>
            </div>
        </div>
    `;
}

/**
 * Render guild stats overview
 */
function renderGuildStats() {
    const unlockedCount = pilferingSkill.getUnlockedPilfererCount();
    const maxCount = PILFERER_UNLOCK_LEVELS.length;
    const activeCount = pilferingSkill.getActivePilfererCount();
    const stats = gameState.getGuildStats();

    // Find next unlock level
    const pilferingLevel = gameState.getSkill(SKILLS.PILFERING).level;
    const nextUnlockLevel = PILFERER_UNLOCK_LEVELS.find(lvl => lvl > pilferingLevel);

    return `
        <div class="guild-stats-panel">
            <div class="guild-stat">
                <span class="stat-icon">🧑‍🤝‍🧑</span>
                <span class="stat-label">Pilferers:</span>
                <span class="stat-value">${activeCount}/${unlockedCount} active</span>
                ${nextUnlockLevel ? `<span class="stat-hint">(Next at Lv ${nextUnlockLevel})</span>` : ''}
            </div>
            <div class="guild-stat">
                <span class="stat-icon">📦</span>
                <span class="stat-label">Items Gathered:</span>
                <span class="stat-value">${formatNumber(stats.totalItemsGathered)}</span>
            </div>
            <div class="guild-stat">
                <span class="stat-icon">📜</span>
                <span class="stat-label">Contract Assists:</span>
                <span class="stat-value">${formatNumber(stats.contractAssistProcs)}</span>
            </div>
        </div>
    `;
}

/**
 * Render all 7 guild domains
 */
function renderGuildDomains() {
    const domainsHtml = Object.values(GUILD_DOMAINS)
        .map(domain => renderPilfererCard(domain))
        .join('');

    return `
        <div class="guild-domains-section">
            <h3 class="section-title">Domains</h3>
            <p class="section-hint">Assign pilferers to gather materials from their contacts</p>
            <div class="guild-domains-grid">
                ${domainsHtml}
            </div>
        </div>
    `;
}

/**
 * Format time remaining in ms to readable string
 */
function formatTimeRemaining(ms) {
    if (ms === null || ms <= 0) return 'Ready!';
    const seconds = Math.ceil(ms / 1000);
    return formatTime(seconds);
}

/**
 * Calculate pilferer time remaining
 */
function getPilfererTimeRemaining(domainId) {
    const pilferer = gameState.getPilferer(domainId);
    if (!pilferer?.active || !pilferer.startedAt) return null;

    const pilferingLevel = gameState.getSkill(SKILLS.PILFERING)?.level || 1;

    // Check both slot-full and effectively full
    const activeDomains = pilferingSkill.getActiveDomains();
    const slotFull = gameState.isGuildChestFull();
    const effectivelyFull = gameState.isChestEffectivelyFull(activeDomains);
    const chestFull = slotFull || effectivelyFull;

    const gatherDuration = getGatherDuration(pilferingLevel, chestFull);

    const elapsed = Date.now() - pilferer.startedAt;
    return Math.max(0, gatherDuration - elapsed);
}

/**
 * Render a single pilferer/domain card
 */
function renderPilfererCard(domain) {
    const pilferer = gameState.getPilferer(domain.id);
    const isActive = pilferer?.active || false;
    const unlockedCount = pilferingSkill.getUnlockedPilfererCount();
    const activeCount = pilferingSkill.getActivePilfererCount();
    const canActivate = activeCount < unlockedCount;

    // Get progress if active
    let progressPercent = 0;
    let currentItem = null;
    let timeRemaining = null;
    if (isActive && pilferer.startedAt) {
        const progressInfo = pilferingSkill.getPilfererProgress(domain.id);
        progressPercent = progressInfo.percent;
        currentItem = progressInfo.itemId;
        timeRemaining = getPilfererTimeRemaining(domain.id);
    }

    const currentItemName = currentItem ? (getItemName(currentItem) || currentItem.replace(/_/g, ' ')) : '';

    // Build list of gatherable items - show base items + tier unlocks
    const tierLevel = pilferingSkill.getPilferingTierLevel();
    const allItems = getDomainItemsAtTier(domain.id, tierLevel);
    const baseItems = new Set(domain.items);

    const itemsList = allItems.map(itemId => {
        const name = getItemName(itemId) || itemId.replace(/_/g, ' ');
        const icon = getItemIcon(itemId) || '•';
        const isTierItem = !baseItems.has(itemId);
        const tierClass = isTierItem ? ' tier-item' : '';
        const tierLabel = isTierItem ? ' (Tier Upgrade)' : '';
        return `<span class="domain-item${tierClass}" title="${name}${tierLabel}">${icon}</span>`;
    }).join('');

    return `
        <div class="pilferer-card ${isActive ? 'active' : ''}" data-domain-id="${domain.id}"
             style="--domain-color: ${domain.color}">
            <div class="pilferer-card-content">
                <div class="pilferer-card-header">
                    <span class="pilferer-icon">${getDomainIcons(domain)}</span>
                    <span class="pilferer-name">${domain.name}</span>
                </div>
                <p class="pilferer-desc">${domain.description}</p>
                <div class="pilferer-items">
                    ${itemsList}
                </div>
            </div>
            <div class="pilferer-card-footer">
                ${isActive ? `
                    <div class="pilferer-status">
                        <span class="status-text">${currentItemName ? `Gathered: ${currentItemName}` : 'Gathering...'}</span>
                        <span class="time-remaining">${formatTimeRemaining(timeRemaining)}</span>
                    </div>
                    <div class="pilferer-progress">
                        <div class="pilferer-progress-fill" style="width: ${progressPercent}%"></div>
                    </div>
                    <button class="pilferer-btn stop" onclick="window.pilferingUI.deactivatePilferer('${domain.id}')">
                        Stop
                    </button>
                ` : `
                    <button class="pilferer-btn start ${canActivate ? '' : 'disabled'}"
                            ${canActivate ? `onclick="window.pilferingUI.activatePilferer('${domain.id}')"` : 'disabled'}>
                        ${canActivate ? 'Assign' : 'No Pilferers'}
                    </button>
                `}
            </div>
        </div>
    `;
}

/**
 * Calculate gather duration for pilferers
 * @param {number} pilferingLevel
 * @param {boolean} chestFull
 * @returns {number} Gather duration in ms
 */
function getGatherDuration(pilferingLevel, chestFull = false) {
    const slowdownRange = PILFERER_SPEED_CONFIG.BASE_SLOWDOWN_PERCENT - PILFERER_SPEED_CONFIG.MIN_SLOWDOWN_PERCENT;
    const slowdownPercent = PILFERER_SPEED_CONFIG.BASE_SLOWDOWN_PERCENT - (pilferingLevel * slowdownRange / 99);

    let effectiveTime = PILFERER_SPEED_CONFIG.BASE_GATHER_TIME * (1 + slowdownPercent / 100);

    // Apply talent pilferer speed bonus (Guild Connections)
    const pilfererSpeedBonus = getTalentFlatBonus(SKILLS.PILFERING, 'pilfererSpeed');
    if (pilfererSpeedBonus > 0) {
        effectiveTime /= (1 + pilfererSpeedBonus);
    }

    // Apply chest full penalty if at 99 architecting
    if (chestFull) {
        const archLevel = gameState.getSkill(SKILLS.ARCHITECTING)?.level || 1;
        if (archLevel >= 99) {
            effectiveTime *= (1 + GUILD_CHEST_CONFIG.FULL_CHEST_SLOWDOWN);
        }
    }

    return effectiveTime;
}

/**
 * Calculate guild chest fill timer estimate
 * Estimates time until chest is full (either all slots used OR at max capacity)
 * @returns {{ timeUntilFull: number|null, slotsRemaining: number, activePilferers: number, maxSlots: number, usedSlots: number, isFull: boolean }}
 */
function calculateGuildChestFillTimer() {
    const chest = gameState.getGuildChest();
    const maxSlots = gameState.getMaxGuildChestSlots();
    const maxStack = gameState.getGuildChestMaxStack();
    const usedSlots = chest.length;
    const slotsRemaining = maxSlots - usedSlots;

    // Build map of items in chest with their current quantities
    const chestItems = new Map();
    for (const slot of chest) {
        chestItems.set(slot.itemId, slot.quantity);
    }

    // Get active pilferers and check if ANY item they produce can be added
    const activePilfererDomains = [];
    let canAddAnyItem = false;

    for (const domain of Object.values(GUILD_DOMAINS)) {
        const pilferer = gameState.getPilferer(domain.id);
        if (pilferer?.active) {
            activePilfererDomains.push(domain);

            // Check if any item from this domain can be added
            for (const itemId of (domain.items || [])) {
                const currentQty = chestItems.get(itemId);
                if (currentQty === undefined) {
                    // Item not in chest - can add if slots available
                    if (slotsRemaining > 0) {
                        canAddAnyItem = true;
                    }
                } else if (currentQty < maxStack) {
                    // Item in chest but not at max stack - can add more
                    canAddAnyItem = true;
                }
            }
        }
    }

    // Chest is "effectively full" if:
    // 1. gameState says it's full (what the game actually uses for slowdown), OR
    // 2. All slots used, OR
    // 3. Active pilferers exist but none of their items can be added
    const hasActivePilferers = activePilfererDomains.length > 0;
    const gameStateIsFull = gameState.isGuildChestFull();
    const isFull = gameStateIsFull || slotsRemaining <= 0 || (hasActivePilferers && !canAddAnyItem);


    // Build set of items already in chest for quick lookup (for time estimate calculation)
    const itemsInChest = new Set(chestItems.keys());
    const activePilferers = activePilfererDomains.length;

    if (activePilferers === 0) {
        guildChestFillEstimate.targetTime = null;
        return {
            timeUntilFull: null,
            slotsRemaining,
            activePilferers: 0,
            maxSlots,
            usedSlots,
            isFull
        };
    }

    // If chest is full (either all slots used OR at max capacity)
    if (isFull) {
        guildChestFillEstimate.targetTime = null;
        return {
            timeUntilFull: 0,
            slotsRemaining: Math.max(0, slotsRemaining),
            activePilferers,
            maxSlots,
            usedSlots,
            isFull: true
        };
    }

    const now = Date.now();

    // Recalculate if conditions changed
    const estimateExpired = guildChestFillEstimate.targetTime && guildChestFillEstimate.targetTime <= now;
    const needsRecalc = !guildChestFillEstimate.targetTime ||
        estimateExpired ||
        slotsRemaining !== guildChestFillEstimate.slotsAtCalculation ||
        activePilferers !== guildChestFillEstimate.activePilferers;

    if (needsRecalc) {
        const pilferingLevel = gameState.getSkill(SKILLS.PILFERING)?.level || 1;
        const gatherDuration = getGatherDuration(pilferingLevel, false);

        // Calculate probability of creating new slots for each active domain
        // Each domain has X items, Y of which are already in chest
        // Probability of new slot per gather = (X - Y) / X
        let totalNewSlotProbability = 0;
        let totalPossibleNewItems = 0;

        for (const domain of activePilfererDomains) {
            const domainItems = domain.items || [];
            const itemsNotInChest = domainItems.filter(item => !itemsInChest.has(item)).length;
            totalPossibleNewItems += itemsNotInChest;

            // Probability this pilferer creates a new slot per gather
            const newSlotChance = domainItems.length > 0 ? itemsNotInChest / domainItems.length : 0;
            totalNewSlotProbability += newSlotChance;
        }

        // If no possible new items from active domains, slots won't fill from these pilferers
        if (totalPossibleNewItems === 0 || totalNewSlotProbability === 0) {
            // Slots can only fill if other items enter - show no estimate
            guildChestFillEstimate.targetTime = null;
        } else {
            // Limit slots to fill to what's actually possible from active domains
            const effectiveSlotsToFill = Math.min(slotsRemaining, totalPossibleNewItems);

            // Expected gathers to fill one new slot = 1 / totalNewSlotProbability
            // Expected gathers to fill all slots = effectiveSlotsToFill / totalNewSlotProbability
            // But probability decreases as items get added, so use harmonic-like series

            // Simulate slot filling with decreasing probability
            let gathersNeeded = 0;
            let simulatedSlotsRemaining = effectiveSlotsToFill;
            let simulatedItemsInChest = new Set(itemsInChest);

            // Track how many items each domain has left to add
            const domainItemsLeft = new Map();
            for (const domain of activePilfererDomains) {
                const left = (domain.items || []).filter(item => !simulatedItemsInChest.has(item));
                domainItemsLeft.set(domain.id, left);
            }

            while (simulatedSlotsRemaining > 0) {
                // Calculate current probability of new slot
                let currentProb = 0;
                for (const domain of activePilfererDomains) {
                    const left = domainItemsLeft.get(domain.id) || [];
                    const total = (domain.items || []).length;
                    if (total > 0) {
                        currentProb += left.length / total;
                    }
                }

                if (currentProb <= 0) break;

                // Expected gathers for next new slot
                const gathersForNext = 1 / currentProb;
                gathersNeeded += gathersForNext;

                // Simulate adding one item (pick from domain with most items left)
                let maxLeft = 0;
                let domainToUpdate = null;
                for (const domain of activePilfererDomains) {
                    const left = domainItemsLeft.get(domain.id) || [];
                    if (left.length > maxLeft) {
                        maxLeft = left.length;
                        domainToUpdate = domain;
                    }
                }

                if (domainToUpdate) {
                    const left = domainItemsLeft.get(domainToUpdate.id);
                    if (left.length > 0) {
                        simulatedItemsInChest.add(left[0]);
                        domainItemsLeft.set(domainToUpdate.id, left.slice(1));
                    }
                }

                simulatedSlotsRemaining--;
            }

            // Convert gathers to time
            // Each pilferer does 1 gather per gatherDuration
            // With N pilferers, gathers happen at rate N/gatherDuration
            const gathersPerMs = activePilferers / gatherDuration;
            const timeToFill = gathersNeeded / gathersPerMs;

            guildChestFillEstimate.targetTime = now + timeToFill;
        }

        guildChestFillEstimate.lastCalculatedAt = now;
        guildChestFillEstimate.slotsAtCalculation = slotsRemaining;
        guildChestFillEstimate.activePilferers = activePilferers;
    }

    const timeUntilFull = guildChestFillEstimate.targetTime
        ? Math.max(0, guildChestFillEstimate.targetTime - now)
        : null;

    return {
        timeUntilFull,
        slotsRemaining,
        activePilferers,
        maxSlots,
        usedSlots,
        isFull
    };
}

/**
 * Render guild chest timer section
 */
function renderGuildChestTimer() {
    const timerData = calculateGuildChestFillTimer();
    const maxStack = gameState.getGuildChestMaxStack();

    // Determine display state - check full FIRST (before no pilferers check)
    let timerClass = '';
    let timerText = '';
    let timerLabelText = 'Est. time until full';
    let timerLabelSuffix = ':';
    let showEstimateInfo = false;

    if (timerData.isFull) {
        // Chest is full (slots or capacity) - show this regardless of active pilferers
        timerClass = 'full';
        timerText = 'Full!';
        timerLabelText = 'Chest status';
    } else if (timerData.activePilferers === 0) {
        // No active pilferers and chest not full
        timerLabelText = 'No active pilferers';
        timerLabelSuffix = '';
        timerText = '--';
    } else if (timerData.timeUntilFull !== null) {
        const seconds = Math.ceil(timerData.timeUntilFull / 1000);
        timerText = formatTime(seconds);
        showEstimateInfo = true;
        // Mark as "soon" if less than 30 minutes
        if (timerData.timeUntilFull < 1800000) {
            timerClass = 'soon';
        }
    } else {
        timerText = '--';
    }

    const fillPercent = Math.round((timerData.usedSlots / timerData.maxSlots) * 100);
    const capacityTooltip = renderInfoIcon(
        `Slots: ${timerData.usedSlots}/${timerData.maxSlots} (${fillPercent}%)\n` +
        `Stack limit: ${maxStack} per slot\n` +
        'Upgrade via Architecting milestones.',
        { size: 'sm' }
    );

    const estimateTooltipHtml = renderInfoIcon(
        'This is an estimate only.\n' +
        'Actual time may vary due to:\n' +
        '• Stack limits on items\n' +
        '• Random item drops from pilfering',
        { size: 'sm' }
    );

    return `
        <div class="chest-timer">
            <span class="timer-icon"></span>
            <span class="timer-label">${timerLabelText}${timerLabelSuffix}</span>
            <span class="timer-value ${timerClass}">${timerText}</span>
            <span class="estimate-tooltip" style="${showEstimateInfo ? '' : 'display: none;'}">${estimateTooltipHtml}</span>
            ${capacityTooltip}
        </div>
    `;
}

/**
 * Render the guild chest
 */
function renderGuildChest() {
    const chestStats = pilferingSkill.getGuildChestStats();
    const chest = chestStats.items;
    const maxSlots = chestStats.maxSlots;
    const usedSlots = chestStats.usedSlots;

    // Chest is full if all slots used OR effectively full (all domain items at max stack)
    const isSlotFull = chestStats.isFull;
    const isEffectivelyFull = chestStats.isEffectivelyFull;
    const isChestFull = isSlotFull || isEffectivelyFull;
    const architectingLevel = gameState.getSkill(SKILLS.ARCHITECTING).level;

    // Create slot array
    const slots = [];
    for (let i = 0; i < maxSlots; i++) {
        slots.push(chest[i] || null);
    }

    const slotsHtml = slots.map((slot, i) => renderGuildChestSlot(slot, i)).join('');

    // Status message
    let statusMessage = '';
    if (isChestFull) {
        if (architectingLevel >= 99) {
            statusMessage = `<span class="chest-status slowed">⚠️ Chest full - Pilferers at 60% reduced speed</span>`;
        } else {
            const fullReason = isEffectivelyFull && !isSlotFull
                ? 'All items at max stack'
                : 'Chest full';
            statusMessage = `<span class="chest-status paused">⚠️ ${fullReason} - Pilferers paused</span>`;
        }
    }

    // Notification threshold setting
    const currentThreshold = gameState.settings.guildChestNotificationThreshold || 'any';
    const thresholdOptions = [
        { value: 'off', label: 'Off' },
        { value: 'any', label: 'Any items' },
        { value: 'half', label: 'Half full' },
        { value: 'full', label: 'Completely full' }
    ];

    const thresholdOptionsHtml = thresholdOptions.map(opt => `
        <label class="notification-option ${currentThreshold === opt.value ? 'active' : ''}">
            <input type="radio" name="guild-chest-notification"
                   value="${opt.value}"
                   ${currentThreshold === opt.value ? 'checked' : ''}
                   onchange="window.pilferingUI.setGuildChestNotificationThreshold('${opt.value}')">
            <span class="option-label">${opt.label}</span>
        </label>
    `).join('');

    const maxStack = gameState.getGuildChestMaxStack();
    const isOverflow = usedSlots > maxSlots;

    return `
        <div class="guild-chest-section ${isOverflow ? 'overflow' : ''}">
            <div class="guild-chest-header">
                <h3 class="section-title">📦 Guild Chest</h3>
                <span class="chest-count ${isOverflow ? 'overflow' : ''}">${usedSlots}/${maxSlots} slots</span>
                <span class="chest-stack-info">(${maxStack} per item)</span>
                ${usedSlots > 0 ? `
                    <button class="chest-claim-all-btn" onclick="window.pilferingUI.claimAllGuildChest()">
                        Claim All
                    </button>
                ` : ''}
            </div>
            ${renderGuildChestTimer()}
            ${statusMessage}
            <div class="guild-chest-grid">
                ${slotsHtml}
            </div>
            <p class="chest-help">
                Click items to claim to inventory
            </p>

            <div class="chest-notification-settings">
                <h4>Sidebar Notification</h4>
                <p class="settings-desc">Show a notification indicator on Pilfering in the sidebar when:</p>
                <div class="notification-options">
                    ${thresholdOptionsHtml}
                </div>
            </div>
        </div>
    `;
}

/**
 * Render a single guild chest slot
 */
function renderGuildChestSlot(slot, index) {
    if (!slot) {
        return `<div class="guild-chest-slot empty"></div>`;
    }

    const itemName = getItemName(slot.itemId) || slot.itemId.replace(/_/g, ' ');
    const itemColor = getItemColor(slot.itemId) || '#555';
    const itemIcon = getItemIcon(slot.itemId, slot.quantity) || '?';

    return `
        <div class="guild-chest-slot filled"
             data-item-id="${slot.itemId}"
             onclick="window.pilferingUI.claimGuildChestItem('${slot.itemId}')"
             title="Click to claim ${formatNumber(slot.quantity)} ${itemName}">
            <div class="guild-chest-slot-content" style="background-color: ${itemColor};">
                <span class="chest-item-icon">${itemIcon}</span>
                <span class="chest-item-qty">${formatNumber(slot.quantity)}</span>
            </div>
        </div>
    `;
}

// ==================== GUILD HANDLERS ====================

/**
 * Unlock the guild
 */
export function unlockGuild() {
    const result = pilferingSkill.unlockGuild();
    if (result.success) {
        toastSuccess('Welcome to the Pilfering Guild!');
        renderPilferingUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Activate a pilferer for a domain
 */
export function activatePilferer(domainId) {
    const result = pilferingSkill.activatePilferer(domainId);
    if (result.success) {
        renderPilferingUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Deactivate a pilferer
 */
export function deactivatePilferer(domainId) {
    const result = pilferingSkill.deactivatePilferer(domainId);
    if (result.success) {
        renderPilferingUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Claim a single item from guild chest
 */
export function claimGuildChestItem(itemId) {
    const result = pilferingSkill.claimFromGuildChest(itemId);
    if (result.success) {
        renderPilferingUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Claim all items from guild chest
 */
export function claimAllGuildChest() {
    const chestStats = pilferingSkill.getGuildChestStats();
    const hadItems = chestStats.usedSlots > 0;

    const result = pilferingSkill.claimAllFromGuildChest();
    if (result.success && result.claimedCount > 0) {
        toastSuccess(`Claimed ${result.claimedCount} item type${result.claimedCount > 1 ? 's' : ''}`);
        renderPilferingUI();
    } else if (hadItems && result.claimedCount === 0) {
        // Chest had items but nothing was claimed - inventory is full
        toastWarning('Inventory full');
    } else if (result.claimedCount === 0) {
        toastWarning('Chest is empty');
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Update pilfering UI each tick
 */
export function updatePilferingUI() {
    // Check if loot multiplier changed (corruption started/ended)
    const currentLootMultiplier = getCorruptionLootMultiplier(SKILLS.PILFERING);
    if (currentLootMultiplier !== lastLootMultiplier) {
        lastLootMultiplier = currentLootMultiplier;
        if (!isTalentTreeOpen()) {
            renderPilferingUI();
        }
        return;
    }

    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.PILFERING);
    const xpPerHour = getXPPerHour(SKILLS.PILFERING);
    const { levelDisplay } = getSkillProgressInfo(SKILLS.PILFERING);

    const xpTotalEl = document.querySelector('.pilfering-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.pilfering-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    const levelEl = document.querySelector('.pilfering-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    updateCompactHeaderProgress('.pilfering-interface', SKILLS.PILFERING);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.pilfering-interface');

    // Handle guild tab updates
    if (currentTab === 'guild') {
        updateGuildUI();
        return;
    }

    // Check for stale progress bars (action stopped)
    const action = gameState.getCurrentAction();
    const isPilferingActive = action && action.type === 'pilfering';

    const existingCards = document.querySelectorAll('.pilfering-interface .target-card.active');
    if (existingCards.length > 0 && !isPilferingActive) {
        renderPilferingUI();
        return;
    }

    // Update action progress if pilfering is active
    if (!isPilferingActive) return;

    const activeCard = document.querySelector(`.target-card[data-target-id="${action.target.id}"]`);
    if (activeCard) {
        const cardProgressFill = activeCard.querySelector('.target-progress-fill');
        if (cardProgressFill) {
            cardProgressFill.style.width = `${action.progress * 100}%`;
        }
    }
}

// Track chest state to detect changes
let lastChestState = '';

// Track chest fill estimate for countdown
let guildChestFillEstimate = {
    targetTime: null,       // When we estimate the chest will be full
    lastCalculatedAt: null, // When we last recalculated
    slotsAtCalculation: 0,  // Slots remaining when calculated
    activePilferers: 0      // Active count when calculated
};

/**
 * Update guild UI each tick
 */
function updateGuildUI() {
    // Update pilferer progress bars and timers
    const pilfererCards = document.querySelectorAll('.pilferer-card.active');
    pilfererCards.forEach(card => {
        const domainId = card.dataset.domainId;
        if (!domainId) return;

        const progressInfo = pilferingSkill.getPilfererProgress(domainId);
        const progressFill = card.querySelector('.pilferer-progress-fill');
        if (progressFill) {
            progressFill.style.width = `${progressInfo.percent}%`;
        }

        // Update last gathered item name if changed
        const statusText = card.querySelector('.status-text');
        if (statusText) {
            if (progressInfo.itemId) {
                const itemName = getItemName(progressInfo.itemId) || progressInfo.itemId.replace(/_/g, ' ');
                statusText.textContent = `Gathered: ${itemName}`;
            } else {
                statusText.textContent = 'Gathering...';
            }
        }

        // Update countdown timer
        const timerEl = card.querySelector('.time-remaining');
        if (timerEl) {
            const timeRemaining = getPilfererTimeRemaining(domainId);
            timerEl.textContent = formatTimeRemaining(timeRemaining);
        }
    });

    // Update chest fill timer
    const timerData = calculateGuildChestFillTimer();
    const timerValueEl = document.querySelector('.guild-chest-section .chest-timer .timer-value');
    const timerLabelEl = document.querySelector('.guild-chest-section .chest-timer .timer-label');

    if (timerValueEl) {
        let timerText = '--';
        let timerClass = '';
        let timerLabelText = 'Est. time until full';
        let timerLabelSuffix = ':';
        let showEstimateInfo = false;

        // Check full FIRST (before no pilferers check)
        if (timerData.isFull) {
            timerClass = 'full';
            timerText = 'Full!';
            timerLabelText = 'Chest status';
        } else if (timerData.activePilferers === 0) {
            timerLabelText = 'No active pilferers';
            timerLabelSuffix = '';
            timerText = '--';
        } else if (timerData.timeUntilFull !== null) {
            const seconds = Math.ceil(timerData.timeUntilFull / 1000);
            timerText = formatTime(seconds);
            showEstimateInfo = true;
            if (timerData.timeUntilFull < 1800000) {
                timerClass = 'soon';
            }
        }

        timerValueEl.textContent = timerText;
        timerValueEl.className = `timer-value ${timerClass}`;

        if (timerLabelEl) {
            const newLabelText = timerLabelText + timerLabelSuffix;
            if (timerLabelEl.textContent !== newLabelText) {
                timerLabelEl.textContent = newLabelText;
            }
        }

        // Show/hide estimate tooltip
        const estimateTooltipEl = document.querySelector('.guild-chest-section .chest-timer .estimate-tooltip');
        if (estimateTooltipEl) {
            estimateTooltipEl.style.display = showEstimateInfo ? '' : 'none';
        }
    }

    // Update guild stats
    const stats = gameState.getGuildStats();
    const itemsGatheredEl = document.querySelector('.guild-stats-panel .guild-stat:nth-child(2) .stat-value');
    if (itemsGatheredEl) {
        itemsGatheredEl.textContent = formatNumber(stats.totalItemsGathered);
    }

    const contractAssistsEl = document.querySelector('.guild-stats-panel .guild-stat:nth-child(3) .stat-value');
    if (contractAssistsEl) {
        contractAssistsEl.textContent = formatNumber(stats.contractAssistProcs);
    }

    // Update chest - check for changes and re-render if needed
    const chest = gameState.getGuildChest();
    const maxSlots = gameState.getMaxGuildChestSlots();

    // Update slot count text and overflow state
    const chestCountEl = document.querySelector('.guild-chest-header .chest-count');
    const isOverflow = chest.length > maxSlots;
    if (chestCountEl) {
        chestCountEl.textContent = `${chest.length}/${maxSlots} slots`;
        chestCountEl.classList.toggle('overflow', isOverflow);
    }

    // Update chest section overflow state
    const chestSection = document.querySelector('.guild-chest-section');
    if (chestSection) {
        chestSection.classList.toggle('overflow', isOverflow);
    }

    // Check if chest contents changed
    const chestState = JSON.stringify(chest);
    if (chestState !== lastChestState) {
        lastChestState = chestState;

        // Re-render the chest grid
        const chestGrid = document.querySelector('.guild-chest-grid');
        if (chestGrid) {
            const slots = [];
            for (let i = 0; i < maxSlots; i++) {
                slots.push(chest[i] || null);
            }
            chestGrid.innerHTML = slots.map((slot, i) => renderGuildChestSlot(slot, i)).join('');
        }

        // Update chest status message
        const chestStats = pilferingSkill.getGuildChestStats();
        const isSlotFull = chestStats.isFull;
        const isEffectivelyFull = chestStats.isEffectivelyFull;
        const isChestFull = isSlotFull || isEffectivelyFull;
        const architectingLevel = gameState.getSkill(SKILLS.ARCHITECTING).level;
        const chestSection = document.querySelector('.guild-chest-section');
        if (chestSection) {
            // Update or add status message
            let statusEl = chestSection.querySelector('.chest-status');
            if (isChestFull) {
                let statusHtml;
                if (architectingLevel >= 99) {
                    statusHtml = `<span class="chest-status slowed">⚠️ Chest full - Pilferers at 60% reduced speed</span>`;
                } else {
                    const fullReason = isEffectivelyFull && !isSlotFull
                        ? 'All items at max stack'
                        : 'Chest full';
                    statusHtml = `<span class="chest-status paused">⚠️ ${fullReason} - Pilferers paused</span>`;
                }
                if (statusEl) {
                    statusEl.outerHTML = statusHtml;
                } else {
                    const header = chestSection.querySelector('.guild-chest-header');
                    if (header) header.insertAdjacentHTML('afterend', statusHtml);
                }
            } else if (statusEl) {
                statusEl.remove();
            }
        }

        // Update Claim All button visibility
        const claimAllBtn = document.querySelector('.chest-claim-all-btn');
        if (chest.length > 0 && !claimAllBtn) {
            const header = document.querySelector('.guild-chest-header');
            if (header) {
                header.insertAdjacentHTML('beforeend', `
                    <button class="chest-claim-all-btn" onclick="window.pilferingUI.claimAllGuildChest()">
                        Claim All
                    </button>
                `);
            }
        } else if (chest.length === 0 && claimAllBtn) {
            claimAllBtn.remove();
        }
    }

    // Update active pilferer count in stats
    const unlockedCount = pilferingSkill.getUnlockedPilfererCount();
    const activeCount = pilferingSkill.getActivePilfererCount();
    const pilfererStatEl = document.querySelector('.guild-stats-panel .guild-stat:first-child .stat-value');
    if (pilfererStatEl) {
        pilfererStatEl.textContent = `${activeCount}/${unlockedCount} active`;
    }
}

/**
 * Setup pilfering skill listeners
 */
export function initPilferingUI() {
    pilferingSkill.initializeStartingTool();
    pilferingSkill.onPickpocketComplete = (target, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Pilfering level ${xpResult.newLevel}!`);
        }

        // Only re-render if pilfering page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.PILFERING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderPilferingUI();
            }
        }
    };
}

// ==================== SIDEBAR NOTIFICATION ====================

/**
 * Check if guild chest notification should show based on settings
 * Uses actual item capacity (slots × stack) not just slot count
 */
export function shouldShowGuildChestNotification() {
    // Only show if guild is unlocked
    if (!gameState.isGuildUnlocked()) return false;

    const threshold = gameState.settings.guildChestNotificationThreshold || 'any';
    if (threshold === 'off') return false;

    const chest = gameState.getGuildChest();
    const maxSlots = gameState.getMaxGuildChestSlots();
    const maxStack = gameState.getGuildChestMaxStack();
    const totalCapacity = maxSlots * maxStack;
    const currentFill = chest.reduce((sum, slot) => sum + slot.quantity, 0);
    const fillPercent = currentFill / totalCapacity;

    // Check if all slots are full (can't add new item types)
    const allSlotsFull = chest.length >= maxSlots;
    // Check if at max capacity (all slots at max stack)
    const atMaxCapacity = currentFill >= totalCapacity;
    // Check if effectively full (all domain items at max stack)
    const chestStats = pilferingSkill.getGuildChestStats();
    const isEffectivelyFull = chestStats.isEffectivelyFull;

    switch (threshold) {
        case 'any':
            return chest.length > 0;
        case 'half':
            return fillPercent >= 0.5;
        case 'full':
            // Trigger when either all slots are used OR at max capacity OR effectively full
            return allSlotsFull || atMaxCapacity || isEffectivelyFull;
        default:
            return false;
    }
}

/**
 * Update the sidebar notification indicator for Pilfering
 */
export function updatePilferingSidebarNotification() {
    const skillItem = document.querySelector('[data-skill="pilfering"]');
    if (!skillItem) return;

    // Only show notification if guild is unlocked
    if (!gameState.isGuildUnlocked()) {
        const existingIndicator = skillItem.querySelector('.skill-notification');
        if (existingIndicator) existingIndicator.remove();
        return;
    }

    const hasNotification = shouldShowGuildChestNotification();

    // Check if chest is actually full - use same logic as timer (gameState + capacity checks)
    // Also check for "effectively full" (all domain items at max stack)
    const chest = gameState.getGuildChest();
    const maxSlots = gameState.getMaxGuildChestSlots();
    const maxStack = gameState.getGuildChestMaxStack();
    const totalCapacity = maxSlots * maxStack;
    const currentFill = chest.reduce((sum, slot) => sum + slot.quantity, 0);
    const chestStats = pilferingSkill.getGuildChestStats();
    const isEffectivelyFull = chestStats.isEffectivelyFull;
    const chestFull = gameState.isGuildChestFull() || chest.length >= maxSlots || currentFill >= totalCapacity || isEffectivelyFull;

    const existingIndicator = skillItem.querySelector('.skill-notification');

    if (chestFull) {
        // Warning state - chest full
        if (existingIndicator) {
            existingIndicator.className = 'skill-notification warning';
            existingIndicator.textContent = '📦';
        } else {
            const indicator = document.createElement('span');
            indicator.className = 'skill-notification warning';
            indicator.textContent = '📦';
            skillItem.appendChild(indicator);
        }
    } else if (hasNotification) {
        // Regular notification - items to collect
        if (existingIndicator) {
            existingIndicator.className = 'skill-notification';
            existingIndicator.textContent = '!';
        } else {
            const indicator = document.createElement('span');
            indicator.className = 'skill-notification';
            indicator.textContent = '!';
            skillItem.appendChild(indicator);
        }
    } else if (existingIndicator) {
        existingIndicator.remove();
    }
}

/**
 * Set guild chest notification threshold
 */
export function setGuildChestNotificationThreshold(threshold) {
    gameState.settings.guildChestNotificationThreshold = threshold;
    updatePilferingSidebarNotification();
    renderPilferingUI();
}

// Window bindings for onclick handlers
if (typeof window !== 'undefined') {
    window.pilferingUI = {
        togglePickpocketing,
        upgradeGloves,
        switchTab,
        activatePilferer,
        deactivatePilferer,
        unlockGuild,
        claimGuildChestItem,
        claimAllGuildChest,
        setGuildChestNotificationThreshold
    };
}

export default {
    renderPilferingUI,
    togglePickpocketing,
    upgradeGloves,
    updatePilferingUI,
    initPilferingUI,
    switchTab,
    getCurrentTab,
    activatePilferer,
    deactivatePilferer,
    unlockGuild,
    claimGuildChestItem,
    claimAllGuildChest,
    shouldShowGuildChestNotification,
    updatePilferingSidebarNotification,
    setGuildChestNotificationThreshold
};
